import React, { useEffect, useRef } from 'react';
import { Typography } from '@mui/material';

const IframeWidget = ({ data, config }) => {
  const iframeRef = useRef(null);
  const refreshIntervalRef = useRef(null);

  const iframeUrl = config?.iframeUrl || data?.iframeUrl;
  const refreshInterval = config?.iframeRefreshInterval || 0;
  const allowScrolling = config?.allowScrolling !== undefined ? config.allowScrolling : true;
  const showBorder = config?.showBorder !== undefined ? config.showBorder : false;

  useEffect(() => {
    // Depend on iframeUrl as well as the interval: the effect requires
    // iframeRef.current, but the iframe is only rendered once a URL exists.
    // Keyed on [refreshInterval] alone, a widget that gained its URL later
    // never re-ran this — auto-refresh silently never started.
    if (refreshInterval <= 0 || !iframeUrl) return undefined;

    // The reload nudge (blank the src, then restore it) is scheduled INSIDE the
    // interval callback, so its timer id has to be tracked separately —
    // clearInterval does not reach it, and it previously kept firing after
    // unmount.
    let reloadTimer = null;

    const intervalId = setInterval(() => {
      const el = iframeRef.current;
      if (!el) return;
      const currentSrc = el.src;
      el.src = '';
      reloadTimer = setTimeout(() => {
        if (iframeRef.current) {
          iframeRef.current.src = currentSrc;
        }
      }, 100);
    }, refreshInterval * 1000);
    refreshIntervalRef.current = intervalId;

    return () => {
      clearInterval(intervalId);
      if (reloadTimer) clearTimeout(reloadTimer);
      refreshIntervalRef.current = null;
    };
  }, [refreshInterval, iframeUrl]);

  if (!iframeUrl) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: '20px'
      }}>
        <Typography variant="body2" color="text.secondary">
          No iframe URL configured. Please edit the widget to add an iframe URL.
        </Typography>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <iframe
        ref={iframeRef}
        src={iframeUrl}
        style={{
          width: '100%',
          height: '100%',
          border: showBorder ? '1px solid #E5E6EB' : 'none',
          borderRadius: showBorder ? '4px' : '0'
        }}
        scrolling={allowScrolling ? 'yes' : 'no'}
        title="Custom Widget Iframe"
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
      />
    </div>
  );
};

export default IframeWidget;
